import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic2, Globe, DollarSign, TrendingUp, Twitter, Instagram, Music2, ArrowRight, Mail, CheckCircle2 } from 'lucide-react';
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
export function HipHopHub() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 30,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 30);
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;
      if (distance < 0) {
        clearInterval(timer);
        return;
      }
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
        seconds: Math.floor(distance % (1000 * 60) / 1000)
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setEmail('');
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <div className="relative min-h-screen bg-[#0A0A0A] text-white font-inter selection:bg-[#FFD400]/30" data-id="element-54">
      {/* Ambient Effects */}
      <div className="fixed inset-0 pointer-events-none" data-id="element-55">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] ambient-glow opacity-40 blur-[120px]" data-id="element-56" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] red-ambient-glow opacity-20 blur-[120px]" data-id="element-57" />
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-58" />
        <div className="noise-overlay" data-id="element-59" />
      </div>

      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5" data-id="element-60">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between" data-id="element-61">
          <div className="flex items-center gap-2 group cursor-pointer" data-id="element-62">
            <img src="https://cdn.magicpatterns.com/uploads/vx6vxvCE7LBQqd61ihcNGX/Screenshot_2026-04-17_at_6.46.04_AM-removebg-preview.png" alt="HipHop Servers Logo" className="h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-63" />
          </div>

          <button onClick={() => document.getElementById('signup')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-white text-black px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-wider hover:bg-[#FFD400] transition-colors" data-id="element-64">
            Notify Me
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 container mx-auto px-6 pt-12 pb-32" data-id="element-65">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-5xl mx-auto text-center" data-id="element-66">
          <motion.div variants={itemVariants} className="inline-block mb-6" data-id="element-67">
            <span className="px-4 py-1.5 rounded-full border border-[#FFD400]/20 bg-[#FFD400]/5 text-[#FFD400] text-xs font-black tracking-[0.2em] uppercase" data-id="element-68">
              The Future of Urban Content
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-poppins font-black tracking-tighter leading-[1.1] mb-8" data-id="element-69">
            BUILD YOUR BRAND. <br data-id="element-70" />
            OWN YOUR AUDIENCE. <br data-id="element-71" />
            <span className="text-[#FFD400]" data-id="element-72">WE'RE LAUNCHING SOON.</span>
          </motion.h1>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-white/60 mb-12 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-73">
            Professional infrastructure for creators, artists, DJs, podcasters,
            and influencers. Launch your own platform. Stop renting your
            audience on social media.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24" data-id="element-74">
            <button onClick={() => document.getElementById('signup')?.scrollIntoView({
            behavior: 'smooth'
          })} className="w-full sm:w-auto bg-[#FFD400] text-black px-10 py-5 rounded-full font-black text-lg uppercase tracking-wider hover:scale-105 transition-transform shadow-[0_0_30px_rgba(255,212,0,0.3)]" data-id="element-75">
              Join Waitlist
            </button>
            <button onClick={() => document.getElementById('signup')?.scrollIntoView({
            behavior: 'smooth'
          })} className="w-full sm:w-auto px-10 py-5 rounded-full font-black text-lg uppercase tracking-wider border-2 border-[#FF3B30]/40 text-white hover:bg-[#FF3B30]/10 transition-colors" data-id="element-76">
              Get Early Access
            </button>
          </motion.div>

          {/* Countdown */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto mb-32" data-id="element-77">
            {[{
            label: 'Days',
            value: timeLeft.days
          }, {
            label: 'Hours',
            value: timeLeft.hours
          }, {
            label: 'Minutes',
            value: timeLeft.minutes
          }, {
            label: 'Seconds',
            value: timeLeft.seconds
          }].map(unit => <div key={unit.label} className="relative group" data-id="element-78">
                <div className="absolute inset-0 bg-[#FFD400]/5 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-79" />
                <div className="relative bg-white/[0.02] border border-white/10 rounded-3xl p-8 backdrop-blur-xl" data-id="element-80">
                  <div className="text-5xl md:text-6xl font-poppins font-black mb-2" data-id="element-81">
                    {String(unit.value).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] tracking-[0.3em] font-black uppercase text-white/40" data-id="element-82">
                    {unit.label}
                  </div>
                </div>
              </div>)}
          </motion.div>

          {/* Email Signup */}
          <motion.div id="signup" variants={itemVariants} className="max-w-xl mx-auto mb-40 scroll-mt-32" data-id="element-83">
            <div className="bg-white/[0.03] border border-white/10 p-2 rounded-3xl md:rounded-full backdrop-blur-md flex flex-col md:flex-row gap-2" data-id="element-84">
              <div className="flex-1 flex items-center px-6 py-3 gap-3" data-id="element-85">
                <Mail className="w-5 h-5 text-white/30" data-id="element-86" />
                <input type="email" placeholder="Enter your email address" className="bg-transparent w-full outline-none text-sm font-bold placeholder:text-white/20" value={email} onChange={e => setEmail(e.target.value)} data-id="element-87" />
              </div>
              <button onClick={handleSubmit} className="bg-[#FFD400] text-black px-8 py-4 rounded-2xl md:rounded-full font-black text-sm uppercase tracking-widest hover:bg-white transition-colors" data-id="element-88">
                {isSubmitted ? 'Added!' : 'Notify Me'}
              </button>
            </div>
            <p className="mt-6 text-[11px] font-black tracking-[0.2em] uppercase text-white/30" data-id="element-89">
              BE FIRST TO KNOW WHEN WE GO LIVE
            </p>
          </motion.div>

          {/* Feature Cards */}
          <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-8 text-left" data-id="element-90">
            {[{
            icon: Globe,
            title: 'Creator Websites',
            desc: 'Custom branded websites for artists & creators built for performance.',
            color: '#FFD400'
          }, {
            icon: DollarSign,
            title: 'Monetization Tools',
            desc: 'Sell merch, subscriptions, tickets & digital products directly to fans.',
            color: '#FF3B30'
          }, {
            icon: TrendingUp,
            title: 'Growth Infrastructure',
            desc: 'Email lists, fan CRM, and direct audience ownership tools.',
            color: '#FFD400'
          }].map((feature, idx) => <div key={idx} className="group p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 hover:border-white/20 transition-all duration-500" data-id="element-91">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`} style={{
              backgroundColor: `${feature.color}10`
            }} data-id="element-92">
                  <feature.icon className="w-7 h-7" style={{
                color: feature.color
              }} data-id="element-93" />
                </div>
                <h3 className="text-2xl font-poppins font-black mb-4" data-id="element-94">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed text-sm font-medium" data-id="element-95">
                  {feature.desc}
                </p>
              </div>)}
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-12" data-id="element-96">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-97">
          <div className="flex items-center gap-6" data-id="element-98">
            <span className="text-[11px] font-black tracking-[0.3em] uppercase text-white/30" data-id="element-99">
              Launching Soon © 2026 HipHopHub. Built for creators.
            </span>
          </div>

          <div className="flex items-center space-x-6" data-id="element-100">
            <a href="#" className="text-white/30 hover:text-[#FFD400] transition-colors" data-id="element-101">
              <Twitter className="w-5 h-5" data-id="element-102" />
            </a>
            <a href="#" className="text-white/30 hover:text-[#FFD400] transition-colors" data-id="element-103">
              <Instagram className="w-5 h-5" data-id="element-104" />
            </a>
            <a href="#" className="text-white/30 hover:text-[#FFD400] transition-colors" data-id="element-105">
              <Music2 className="w-5 h-5" data-id="element-106" />
            </a>
          </div>
        </div>
      </footer>
    </div>;
}